import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, getToken, defaultExpiryDate, slugifyFilename } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import BranchMultiSelect from "@/components/BranchMultiSelect";
import { toast } from "sonner";
import { Plus, Download, Ban, Pencil } from "lucide-react";

const blankCoupon = () => ({
    name: "", code: "", discount_type: "percent", discount_value: 10,
    max_uses: 1, restrictions: "", expiry_date: defaultExpiryDate(12), cost_shop_id: "none", allowed_branch_ids: []
});

export default function Coupons() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(blankCoupon());
    const [costShops, setCostShops] = useState([]);
    const [branches, setBranches] = useState([]);

    const load = async () => setItems((await api.get("/admin/coupons")).data);
    useEffect(() => {
        (async () => {
            const [cps, cs, br] = await Promise.all([
                api.get("/admin/coupons"),
                api.get("/admin/cost-shops"),
                api.get("/admin/branches"),
            ]);
            setItems(cps.data);
            setCostShops(cs.data);
            setBranches(br.data);
        })();
    }, []);

    const openCreate = () => { setEditing(null); setForm(blankCoupon()); setOpen(true); };
    const openEdit = (c) => {
        setEditing(c);
        setForm({
            name: c.name,
            code: c.code,
            discount_type: c.discount_type,
            discount_value: c.discount_value,
            max_uses: c.max_uses,
            restrictions: c.restrictions || "",
            expiry_date: c.expiry_date ? c.expiry_date.slice(0, 10) : "",
            cost_shop_id: c.cost_shop_id || "none",
            allowed_branch_ids: c.allowed_branch_ids || [],
        });
        setOpen(true);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editing) {
                const payload = {
                    name: form.name,
                    restrictions: form.restrictions,
                    expiry_date: form.expiry_date || null,
                    cost_shop_id: form.cost_shop_id === "none" ? null : form.cost_shop_id,
                    allowed_branch_ids: form.allowed_branch_ids,
                    max_uses: parseInt(form.max_uses, 10),
                };
                await api.patch(`/admin/coupons/${editing.id}`, payload);
                toast.success("Coupon updated");
            } else {
                const payload = { ...form };
                payload.discount_value = parseFloat(payload.discount_value);
                payload.max_uses = parseInt(payload.max_uses, 10) || 1;
                if (!payload.expiry_date) delete payload.expiry_date;
                if (!payload.code) delete payload.code;
                if (payload.cost_shop_id === "none") payload.cost_shop_id = null;
                await api.post("/admin/coupons", payload);
                toast.success("Coupon created");
            }
            setOpen(false);
            setEditing(null);
            setForm(blankCoupon());
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const onVoid = async (id) => {
        if (!window.confirm("Void this coupon?")) return;
        await api.post(`/admin/coupons/${id}/void`); load();
    };

    const downloadQr = (c) => {
        const token = getToken();
        fetch(`${api.defaults.baseURL}/admin/coupons/${c.id}/qr`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.blob()).then(b => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `${slugifyFilename(c.name)}.png`; a.click(); });
    };

    return (
        <div className="p-8 max-w-7xl" data-testid="coupons-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="text-xs tracking-widest text-[#7A726D] uppercase">Discounts</div>
                    <h1 className="font-display text-4xl font-bold text-espresso tracking-tight">Coupons</h1>
                </div>
                <Button onClick={openCreate} className="bg-espresso rounded-sm" data-testid="new-coupon-button"><Plus className="w-4 h-4 mr-1"/>New coupon</Button>
                <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(blankCoupon()); } }}>
                    <DialogContent className="rounded-sm">
                        <DialogHeader><DialogTitle className="font-display text-espresso">{editing ? "Edit coupon" : "Create coupon"}</DialogTitle></DialogHeader>
                        <form onSubmit={onSubmit} className="space-y-3" data-testid="coupon-form">
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Name</Label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-sm mt-1" data-testid="coupon-name-input"/>
                            </div>
                            {!editing && (
                                <div>
                                    <Label className="text-xs uppercase text-[#7A726D]">Code (optional)</Label>
                                    <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="auto-generated" className="rounded-sm mt-1 uppercase" data-testid="coupon-code-input"/>
                                </div>
                            )}
                            {!editing && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs uppercase text-[#7A726D]">Type</Label>
                                        <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                                            <SelectTrigger className="rounded-sm mt-1" data-testid="coupon-type-select"><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="percent">Percent off</SelectItem>
                                                <SelectItem value="amount">Rand off</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase text-[#7A726D]">Value</Label>
                                        <Input type="number" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="rounded-sm mt-1" data-testid="coupon-value-input"/>
                                    </div>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs uppercase text-[#7A726D]">Max uses</Label>
                                    <Input type="number" min="1" max="1000" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="rounded-sm mt-1" data-testid="coupon-uses-input"/>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase text-[#7A726D]">Expiry</Label>
                                    <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="rounded-sm mt-1" data-testid="coupon-expiry-input"/>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Cost shop</Label>
                                <Select value={form.cost_shop_id} onValueChange={(v) => setForm({ ...form, cost_shop_id: v })}>
                                    <SelectTrigger className="rounded-sm mt-1" data-testid="coupon-cost-shop-select"><SelectValue placeholder="Optional"/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— None —</SelectItem>
                                        {costShops.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Redeemable at</Label>
                                <BranchMultiSelect
                                    branches={branches}
                                    value={form.allowed_branch_ids}
                                    onChange={(v) => setForm({ ...form, allowed_branch_ids: v })}
                                    testid="coupon-branches-select"
                                />
                                <div className="text-xs text-[#7A726D] mt-1">Leave empty to allow all branches.</div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Restrictions</Label>
                                <Textarea value={form.restrictions} onChange={(e) => setForm({ ...form, restrictions: e.target.value })} className="rounded-sm mt-1" data-testid="coupon-restrictions-input"/>
                            </div>
                            <Button type="submit" className="bg-espresso w-full rounded-sm" data-testid="coupon-submit-button">
                                {editing ? "Save changes" : "Create"}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
            <Card className="border-[#E5E0D8] shadow-none rounded-sm">
                <CardContent className="p-0">
                    <Table data-testid="coupons-table">
                        <TableHeader>
                            <TableRow className="bg-[#F9F8F6]">
                                <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Discount</TableHead><TableHead>Uses</TableHead><TableHead>Expiry</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-[#7A726D] py-8">No coupons</TableCell></TableRow>}
                            {items.map((c) => (
                                <TableRow key={c.id}>
                                    <TableCell className="font-mono text-xs">
                                        <Link to={`/admin/coupons/${c.id}`}
                                            className="text-bc-orange hover:underline"
                                            data-testid={`coupon-code-link-${c.code}`}>
                                            {c.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <Link to={`/admin/coupons/${c.id}`}
                                            className="hover:text-bc-orange"
                                            data-testid={`coupon-name-link-${c.code}`}>
                                            {c.name}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{c.discount_type === "percent" ? `${c.discount_value}%` : `R${c.discount_value}`}</TableCell>
                                    <TableCell>{c.uses_remaining}/{c.max_uses}</TableCell>
                                    <TableCell>{c.expiry_date ? c.expiry_date.slice(0, 10) : "—"}</TableCell>
                                    <TableCell><StatusBadge status={c.status}/></TableCell>
                                    <TableCell className="text-right space-x-2">
                                        {c.status === "active" && (
                                            <button onClick={() => openEdit(c)} className="text-[#C48464] inline-flex" data-testid={`coupon-edit-${c.code}`}><Pencil className="w-4 h-4"/></button>
                                        )}
                                        <button onClick={() => downloadQr(c)} className="text-[#7A726D] hover:text-espresso inline-flex" data-testid={`coupon-qr-${c.code}`}><Download className="w-4 h-4"/></button>
                                        {c.status === "active" && <button onClick={() => onVoid(c.id)} className="text-[#B24A45] inline-flex" data-testid={`coupon-void-${c.code}`}><Ban className="w-4 h-4"/></button>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
